import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { recipientListApi } from "./api";
import { toast } from "@shared/ui/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";

export const useGetAllRecipientList = (
  page?: any,
  limit?: any,
  search?: any,
  audienceType?: any,
  listType?: any,
  status?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllRecipientList(
      page,
      limit,
      search,
      audienceType,
      listType,
      status
    ),
    () =>
      recipientListApi.getAllRecipientList(
        utilityId,
        page,
        limit,
        search,
        audienceType,
        listType,
        status
      ),
    {
      enabled: !!utilityId,
    }
  );
};
export const useGetRecipientDetails = (listId: number) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getRecipientDetails(listId),
    () => recipientListApi.getRecipientDetails(utilityId, listId),
    {
      enabled: !!listId,
    }
  );
};

export const useGetRecipientListSummary = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getRecipientListSummary(),
    () => recipientListApi.getRecipientListSummary(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllCustomerMemberList = (
  segmentId: number,
  page?: any,
  limit?: any,
  search?: any,
  filterType?: string,
  area?: string[],
  subArea?: string[],
  category?: string[],
  subCategory?: string[],
  status?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllCustomerMemberList(
      segmentId,
      page,
      limit,
      search,
      filterType,
      area,
      subArea,
      category,
      subCategory,
      status
    ),
    () =>
      recipientListApi.getAllCustomerMemberList(
        utilityId,
        segmentId,
        page,
        limit,
        search,
        filterType,
        area,
        subArea,
        category,
        subCategory,
        status
      ),
    {
      enabled: !!segmentId,
    }
  );
};

export const useGetAllSegments = (audienceType?: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllSegments(audienceType),
    () => recipientListApi.getAllSegments(utilityId, audienceType),
    {
      enabled: !!utilityId,
    }
  );
};

export const useCreateRecipientList = () => {
  return useSmartMutation(
    (payload: any) => recipientListApi.createRecipientList(payload),
    {
      onSuccess: () => {
        toast({
          title: "Recipient List create",
          description: "Recipient List created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "recipientList", "list"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create Recipient List`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateRecipientList = (id: number) => {
  return useSmartMutation(
    (payload: any) => recipientListApi.updateRecipientList(payload, id),
    {
      onSuccess: () => {
        toast({
          title: "Recipient List update",
          description: "Recipient List updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "recipientList", "list"],
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "recipientDetails", "details"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update Recipient List`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUserInfoSearch = (
  segmentId: number,
  filterType: string,
  searchTerm: string,
  page: number,
  limit: number,
  department?: string[],
  roles?: string[],
  status?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.userInfoSearch(
      segmentId,
      filterType,
      searchTerm,
      utilityId,
      page,
      limit,
      department,
      roles,
      status
    ),

    () =>
      recipientListApi.getUserInfo({
        remoteUtilityId: utilityId,
        segmentId,
        filterType,
        page,
        limit,
        searchData: searchTerm,
        departments: department,
        roles: roles,
        status: status,
      }),

    {
      enabled: !!segmentId,
    }
  );
};

export const useGetContactInfo = (listId: number) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getContactInfo(listId),
    () => recipientListApi.getContactInfo(utilityId, listId),
    {
      enabled: !!listId,
    }
  );
};

export const useGetAllRecipientData = (
  listId: number,
  page?: any,
  limit?: any,
  search?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllRecipientData(
      listId,
      page,
      limit,
      search
    ),
    () =>
      recipientListApi.getAllRecipientData(
        listId,
        utilityId,
        page,
        limit,
        search
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllRecipientUsageHistory = (
  listId: number,
  page?: any,
  limit?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllRecipientUsageHistory(
      listId,
      page,
      limit
    ),
    () =>
      recipientListApi.getAllRecipientUsageHistory(
        listId,
        utilityId,
        page,
        limit
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllDepartments = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllDepartment(),
    () => recipientListApi.getAllDepartments(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllRoles = (department: string[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.getAllRoles(department),
    () => recipientListApi.getAllRoles(utilityId, department),
    {
      enabled: !!department,
    }
  );
};

export const useCreateDuplicateRecipientList = () => {
  return useSmartMutation(
    (payload: any) => recipientListApi.createDuplicateRecipientList(payload),
    {
      onSuccess: () => {
        toast({
          title: "Recipient List duplicate",
          description: "Recipient List duplicated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "recipientList", "list"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to duplicate Recipient List`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useStatus = (params: {}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ux.recipientList.status(),
    () => recipientListApi.getStatus(),
    {
      staleTime: 2 * 60 * 1000,
      refetchInterval: 30000,
    }
  );
};

export const useAreaList = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.ux.recipientList.areaList()],
    () => recipientListApi.getTerritoryConfigMap("area", remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useSubAreaList = (areaList: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.mx.readCycleManagement.subAreaList(areaList)],
    () =>
      recipientListApi.getTerritoryConfigMap(
        "sub_area",
        remoteUtilityId,
        areaList
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCategoryConfigMap = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.ux.recipientList.category()],
    () => recipientListApi.getCategoryConfigMap(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useSubCategoryConfigMap = (codes: string[]) => {
  return useSmartQuery(
    [QueryKeyFactory.module.ux.recipientList.subCategory(), codes],
    () => recipientListApi.getSubCategoryConfigMap(codes),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};